import { useState } from 'react';

const footerCategories = [
  { name: 'Accessories', url: 'https://kaspreadsheet.com/search-920/name/Shoes.html' },
  { name: 'Bags', url: 'https://kaspreadsheet.com/search-921/name/Shoes.html' },
  { name: 'Belts', url: 'https://kaspreadsheet.com/search-922/name/Shoes.html' },
];

const socialLinks = [
  { name: 'Instagram', url: '#', icon: 'ri-instagram-line' },
  { name: 'Pinterest', url: '#', icon: 'ri-pinterest-line' },
  { name: 'TikTok', url: '#', icon: 'ri-tiktok-line' },
];

export default function Footer() {
  const [email, setEmail] = useState('');
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    // Honeypot check
    const honeypot = form.querySelector<HTMLInputElement>('input[name="website_alt"]');
    if (honeypot && honeypot.value.trim() !== '') {
      setFormStatus('success');
      setFormMessage('Thank you for subscribing!');
      setEmail('');
      return;
    }

    setFormStatus('submitting');
    setFormMessage('');

    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9aggv4nib0mhnm9k4f0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('Thank you for subscribing! Check your inbox for the latest guides.');
        setEmail('');
      } else if (serverMsg && typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam')) {
        setFormStatus('error');
        setFormMessage('Something went wrong. Please try again.');
      } else {
        setFormStatus('error');
        setFormMessage(typeof serverMsg === 'string' ? serverMsg : 'Something went wrong. Please try again.');
      }
    } catch {
      setFormStatus('error');
      setFormMessage('Network error. Please check your connection and try again.');
    }
  };

  return (
    <footer className="w-full bg-accent-900 py-16 md:py-20 px-4 md:px-8 lg:px-14">
      <div className="max-w-6xl mx-auto">
        {/* Top grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8">
          {/* Column 1: Brand */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <span className="w-8 h-8 rounded-full bg-background-50/20 flex items-center justify-center">
                <i className="ri-database-2-line text-background-50/80 text-base w-4 h-4 flex items-center justify-center"></i>
              </span>
              <span className="font-heading text-base font-bold text-background-50 whitespace-nowrap">Superbuy Sheet</span>
            </div>
            <p className="text-xs text-background-50/50 leading-relaxed max-w-xs">
              Your trusted platform for discovering, comparing and understanding products across all major categories. Updated weekly with fresh resources and guides.
            </p>
          </div>

          {/* Column 2: Newsletter */}
          <div>
            <h4 className="text-xs font-label font-semibold text-background-50/60 uppercase tracking-wider mb-4 whitespace-nowrap">Newsletter</h4>
            <form onSubmit={handleSubmit} data-readdy-form="" className="relative">
              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="newsletter-website-alt-field"
              />
              <div className="flex border-b border-background-50/20 focus-within:border-background-50/50 transition-colors">
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your email address"
                  required
                  className="w-full bg-transparent text-sm text-background-50 placeholder-background-50/30 py-2 outline-none font-label"
                />
                <button
                  type="submit"
                  disabled={formStatus === 'submitting'}
                  className="text-background-50/60 hover:text-background-50 transition-colors cursor-pointer px-2 whitespace-nowrap"
                >
                  <i className="ri-arrow-right-line text-sm w-3.5 h-3.5 flex items-center justify-center"></i>
                </button>
              </div>
              {(formStatus === 'success' || formStatus === 'error') && (
                <p className={`mt-2 text-xs ${formStatus === 'success' ? 'text-accent-200' : 'text-red-300'}`}>
                  {formMessage}
                </p>
              )}
              <p className="mt-2 text-xs text-background-50/30">
                By subscribing, you agree to our{' '}
                <a href="#" className="underline hover:text-background-50/50 transition-colors cursor-pointer">Privacy Policy</a>
              </p>
            </form>
          </div>

          {/* Column 3: Quick Categories */}
          <div>
            <h4 className="text-xs font-label font-semibold text-background-50/60 uppercase tracking-wider mb-4 whitespace-nowrap">Quick Categories</h4>
            <ul className="space-y-2.5">
              {footerCategories.map((cat) => (
                <li key={cat.name}>
                  <a
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="text-sm text-background-50/60 hover:text-background-50 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {cat.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 4: Social */}
          <div>
            <h4 className="text-xs font-label font-semibold text-background-50/60 uppercase tracking-wider mb-4 whitespace-nowrap">Connect</h4>
            <ul className="space-y-2.5">
              {socialLinks.map((link) => (
                <li key={link.name}>
                  <a
                    href={link.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-2 text-sm text-background-50/60 hover:text-background-50 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className={`${link.icon} text-sm w-3.5 h-3.5 flex items-center justify-center`}></i>
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom: Large brand text */}
        <div className="mt-16 md:mt-20">
          <h3 className="font-heading text-5xl md:text-7xl lg:text-8xl font-black text-background-50/10 tracking-tighter leading-none select-none">
            SUPERBUY SHEET
          </h3>
          <div className="mt-8 pt-6 border-t border-background-50/10 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-xs text-background-50/30 whitespace-nowrap">
              &copy; {new Date().getFullYear()} Superbuy Sheet. All rights reserved.
            </p>
            <div className="flex items-center gap-6">
              <a href="#" className="text-xs text-background-50/30 hover:text-background-50/50 transition-colors cursor-pointer whitespace-nowrap">Privacy</a>
              <a href="#" className="text-xs text-background-50/30 hover:text-background-50/50 transition-colors cursor-pointer whitespace-nowrap">Terms</a>
              <a href="#" className="text-xs text-background-50/30 hover:text-background-50/50 transition-colors cursor-pointer whitespace-nowrap">Contact</a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}